import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useDocumentTitle } from '@mantine/hooks'
import { Banner } from '@node-space/storybook-components/dist/Banner'
import LoginForm from './components/LoginForm'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import useAuthRedirect from 'utils/auth/hooks/useAuthRedirect'
import {
  RecaptchaVersionType,
  useRecaptchaVersioning,
} from 'hooks/recaptcha/useRecaptchaVersioning'
import { deleteAuthCookie } from 'utils/auth/utils'

export type RecaptchaProps = {
  version: RecaptchaVersionType
  isLoading: boolean
  getToken: () => Promise<string>
}

export const LoginDeveloper = () => {
  useDocumentTitle(`${process.env.SITE_TITLE}`)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const token = useAuthRedirect()
  const queryClient = useQueryClient()

  const { recaptchaVersion, isLoadingRecaptcha, getRecaptchaToken, resetRecaptcha } =
    useRecaptchaVersioning('login')

  const recaptcha: RecaptchaProps = {
    version: recaptchaVersion,
    isLoading: isLoadingRecaptcha,
    getToken: getRecaptchaToken,
  }

  const invalidateQueries = async () => {
    try {
      await queryClient.invalidateQueries(reactQueryKeys.profile())
      await queryClient.invalidateQueries(reactQueryKeys.verification)
    } catch (error) {
      console.error('Error: invalidateQueries', error)
    }
  }

  const setSuccessfulLogin = async () => {
    try {
      resetRecaptcha()
      await invalidateQueries()

      navigate('/')
    } catch (error) {
      console.error('Error: setSuccessfulLogin', error)
    }
  }

  useEffect(() => {
    if (!token) {
      deleteAuthCookie()
    }
  }, [token])

  return (
    <>
      <Banner
        state="warning"
        message={t('developerLoginBanner')}
        linkLabel={t('verifiedLogin')}
        link={`${process.env.MERCHANT_PORTAL_URL}/login`}
      />

      <LoginForm setSuccessfulLogin={setSuccessfulLogin} recaptcha={recaptcha} />
    </>
  )
}
